import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom';
import { base64Encode, encodeId } from '@app/helpers/base64url';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { deleteCentreById, getCentresWithPagination } from '@app/services/centre';
import DataTable, { TableStyles } from 'react-data-table-component';
import { Button } from '@app/components';
import { getAllActiveFranchise } from '@app/services/franchise';
import { getPostcodesByFranchiseId } from '@app/services/postcode';
import Select from "react-select";
import ExcelJS from "exceljs/dist/es5/exceljs.browser";
import moment from 'moment';
import fileDownload from 'js-file-download';

const ListCenters = () => {

    const isMounted = useRef(true);
    useEffect(() => {
        return () => { isMounted.current = false; };
    }, []);

    const selectRef = useRef(null);
    const [centers, setCenters] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');
    const [search, setSearch] = useState({name: '', franchiseId: '', postcodeId: '', areaCode: '', email: '', status: ''});
    const [franchises, setFranchises] = useState([]);
    const [postcodes, setPostcodes] = useState([]);
    const centreStatus = [{label:'Active', value:'active'}, {label:'Inactive', value:'inactive'}, {label:'Archived', value:'archived'}]
    const canAccess = useSelector((state: any) => state.auth.permissions?.centre);

    const customStyle: TableStyles = {
        head: {
            style: {
                color: "#5E5873",
                fontSize: "13px",
                fontWeight: 900,
            },
        },
        headCells: {
            style: {
                borderColor: "#E9ECEF",
                textTransform: "uppercase",
                letterSpacing: "1px",
            },
        },
        headRow: {
            style: {
                backgroundColor: "#F7F7F8",
                minHeight: "44px",
                fontWeight: 500,
            },
        },
    };

    const customStylesSelect = {
        dropdownIndicator: () => ({
            display: 'none',
        }),
    }

    useEffect(() => {
        getAllCentersData(1, perPage, sortField, sortDirection);
    },[search]);

    useEffect(() => {
        getAllActiveFranchiseData();
        getPostcodesByFranchiseData('all');
    },[]);

    const getAllCentersData = async (page, perPage, sortField, sortDirection) => {
        try {
            setLoading(true);
            const usersResp = await getCentresWithPagination(page, perPage, sortField, sortDirection, search);
            if (!isMounted.current) return;
            if (usersResp.success === true) {
                setLoading(false);
                setCenters(usersResp.collections.data);
                setTotalRows(usersResp?.collections?.pagination?.total);
            } else {
                setLoading(false);
                toast.error(usersResp?.errors[0]?.msg);
            }
        } catch (error: any) {
            if (!isMounted.current) return;
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const getAllActiveFranchiseData = async () => {
        try {
            const resp = await getAllActiveFranchise();
            if (!isMounted.current) return;
            if (resp?.success === true) {
                setFranchises(resp?.collections);
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            if (!isMounted.current) return;
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const getPostcodesByFranchiseData = async (franchiseId) => {
        try {
                if(franchiseId){
                    const resp = await getPostcodesByFranchiseId(franchiseId);
                    if (!isMounted.current) return;
                    if (resp?.success === true) {
                        setPostcodes(resp?.collections);
                    } else {
                        toast.error(resp?.errors[0]?.msg);
                    }
                }
        } catch (error: any) {
            if (!isMounted.current) return;
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const columns = [
        {
            name: 'Centre Name',
            selector: (row: { name: string; }) => row.name,
            sortable: true,
            sortField: 'name',
            wrap: true,
        },
        {
            name: 'Franchise',
            selector: (row: { franchise_name: string; }) => row.franchise_name,
            sortable: true,
            sortField: 'franchise_name',
            wrap: true,
        },
        {
            name: 'Postcode',
            selector: (row: { postcode_name: string; }) => row.postcode_name,
            sortable: true,
            sortField: 'postcode_name',
        },
        {
            name: 'Area Code',
            selector: (row: { area_code: string; }) => row.area_code,
            sortable: true,
            sortField: 'area_code',
        },
        {
            name: 'Email',
            selector: (row: { email: string; }) => row.email ? row.email : '---',
            sortable: true,
            sortField: 'email',
            wrap: true,
        },
        {
            name: 'User(s)',
            cell: (row: { franchisee: any[]; }) => {
                if (!row?.franchisee || row.franchisee.length === 0) return <span>---</span>;

                // Group users by role, since the same user can appear once per
                // role they hold at this centre (e.g. Centre Manager and Mentor).
                const groupsByRole: Record<number, { roleName: string; users: any[] }> = {};
                row.franchisee.forEach((user: any) => {
                    if (!groupsByRole[user.role_id]) {
                        groupsByRole[user.role_id] = { roleName: user.role_name, users: [] };
                    }
                    groupsByRole[user.role_id].users.push(user);
                });

                return (
                    <div>
                        {Object.values(groupsByRole).map((group) => (
                            <div key={group.roleName} className="mb-1">
                                <div className="text-muted small">{group.roleName}:</div>
                                <div>{group.users.map((u: any) => `${u.first_name} ${u.last_name}`).join(", ")}</div>
                            </div>
                        ))}
                    </div>
                );
            },
            wrap: true,
        },
        {
            name: 'Status',
            selector: (row: { status: string; }) => row.status,
            cell: (row: { status: string; }) => (
                row.status === 'active' ? <span className="badge bg-success">Active</span> : <span className="capitalize badge bg-danger">{(centreStatus.find(obj => { return obj?.value === row?.status}))?.label}</span>
            ),
            sortable: true,
            sortField: 'status',
        },
        {  
            name: 'Action',
            cell: (row: { id: number; name: string; }) => (
              <>
                { canAccess?.edit &&
                    <Link
                    to={`/centers/edit/${encodeId(row.id)}`}
                    role="button"
                    state={{ id: row.id }}
                    data-toggle="tooltip" title="Edit"
                    >
                    <i className="fas fa-pen mr-2"/>
                    </Link>
                }
                { canAccess?.view &&
                    <Link
                    to={`/centers/manage-staff/${encodeId(row.id)}`}
                    role="button"
                    state={{ id: row.id }}
                    data-toggle="tooltip" title="Manage Centre Staff/Users"
                    >
                    <i className="fas fa-users-cog mr-2"/>
                    </Link>
                }
                { canAccess?.delete &&
                    <a href='#' onClick={ (e) => { deleteCenterConfirm(e, row.id)} } data-toggle="tooltip" title="Delete">
                        <i className="fas fa-trash mr-2"/>
                    </a>
                }
                { canAccess['page-content'] &&
                    <Link
                    to={`/centers/edit/page-content/${row.id}`}
                    role="button"
                    state={{ id: row.id }}
                    data-toggle="tooltip" 
                    title="Update Page Content"
                    >
                    <i className="fas fa-pager mr-2"/>
                    </Link>
                }
                { canAccess?.['custom-messages'] &&
                    <Link
                    to={`/centres/custom-messages/${base64Encode(JSON.stringify({ centre_id: row.id, centre_name: row.name }))}`}
                    role="button"
                    state={{ centre_id: row.id, centre_name: row.name }}
                    data-toggle="tooltip" 
                    title="Custom Messages"
                    >
                    <i className="fas fa-comment-dots mr-2"/>
                    </Link>
                }
              </>
            ),
        },
    ];

    const deleteCenterConfirm = (e, id) => {
        confirmAlert({
            title: 'Confirm to submit',
            message: 'Are you sure to delete this?',
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => deleteCenter(id)
                },
                {
                    label: 'No',
                    onClick: () => {}
                }
            ]
        });
        e.preventDefault();
    };

    const deleteCenter = async (id) => {
        try {
            setLoading(true);
            const resp = await deleteCentreById(id);
            if(resp.success === true){
                toast.success(resp?.message);
                setLoading(false);
                getAllCentersData(currentPage, perPage, sortField, sortDirection);
            }else{
                toast.error(resp?.errors[0]?.msg);
                setLoading(false);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const handlePageChange = page => {
        getAllCentersData(page, perPage, sortField, sortDirection);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        getAllCentersData(page, newPerPage, sortField, sortDirection);
        setPerPage(newPerPage);
    };

    const handleSort = async (column, sortDirection) => {
        setSortField(column.sortField);
        setSortDirection(sortDirection);
        getAllCentersData(currentPage, perPage, column.sortField, sortDirection);
    };

    const handleResetFilter = async () => {
        selectRef.current.setValue('');
        getPostcodesByFranchiseData('all')
        setSearch({name: '', franchiseId: '', postcodeId: '', areaCode: '', email: '', status: ''});
    };

    const onChangeFranchise = (option) => {
        setSearch({ ...search, franchiseId: option.id });
        getPostcodesByFranchiseData(option.id);
    };

    const exportCSV = async() => {
        try {
            setLoading(true);
            let records = await getCentresWithPagination(1, totalRows, sortField, sortDirection, search)
            if (records.success === true) {
                setLoading(false);
            } else {
                setLoading(false);
                toast.error(records?.errors[0]?.msg);
                return;
            }

            if((records?.collections?.data).length === 0){
                toast.error('Records not found.')
                return;
            }

            const workbook = new ExcelJS.Workbook();
            workbook.addWorksheet("Centres");
            const worksheet = workbook.getWorksheet('Centres');
            worksheet.columns = [
                { header: "Center Name", key: "name" },
                { header: "Franchise", key: "franchise_name" },
                { header: "Postcode", key: "postcode_name" },
                { header: "Area Code", key: "area_code" },
                { header: "Email", key: "email" },
                { header: "Mobile", key: "phone_number" },
                { header: "Address", key: "address" },
                { header: "User(s)", key: "franchisee" },
                { header: "Status", key: "status" },
            ];
            
            (records?.collections?.data).map((row) => {
                row.name = row.name !== null ? row.name : '---';
                row.franchise_name = row.franchise_name !== null ? row.franchise_name : '---';
                row.postcode_name = row.postcode_name !== null ? row.postcode_name : '---';
                row.area_code = (row.area_code != null) ? row.area_code : '---';
                row.email = (row.email != null) ? row.email : '---';
                row.phone_number = (row.phone_number != null) ? row.phone_number : '---';
                row.address = (row.address != null) ? row.address : '---';
                row.franchisee = row.franchisee.length > 0 ? ( Array.prototype.map.call(row.franchisee, function(user) { return user.first_name +' '+ user.last_name; }).join(", ") ) : '---';
                row.status = row.status.charAt(0).toUpperCase() + row.status.slice(1);
            })

            worksheet.addRows(records?.collections?.data);
            let filename = 'Centres-' + moment().format('DD-MMM-YYYY') + '.csv';

            workbook.csv.writeBuffer().then(function(buffer) {
                fileDownload(buffer, filename);
            });
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    }

    return (
        <div>
            <div>
                {/* Content Header (Page header) */}
                <section className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1>Manage Centres</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                                    <li className="breadcrumb-item active">Manage Centres</li>
                                </ol>
                            </div>
                        </div>
                    </div>{/* /.container-fluid */}
                </section>
                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div style={{ marginBottom: 10 }} className="row">
                                    
                                </div>
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Manage Centres</h3>
                                        <div className="col-lg-12 text-right">
                                        { canAccess?.create &&
                                            <Link className="btn btn-primary" to="/centers/add" > + Add New Centre </Link>
                                        }
                                        </div>
                                    </div>
                                    {/* /.card-header */}
                                    <div className="card-body">
                                        <div className="row mb-2">
                                            <div className="col-sm-2">
                                                <input type="text" name='name' className="form-control" value={search.name} onChange={(e) => setSearch({ ...search, name: e.target.value })} placeholder="Search By Centre Name" />
                                            </div>
                                            <div className="col-sm-2">
                                                <Select
                                                    ref={selectRef}
                                                    name="franchiseId"
                                                    placeholder="Search By Franchise"
                                                    options={franchises}
                                                    getOptionValue={(option)=>`${option['id']}`}
                                                    getOptionLabel={(option)=>`${option['name']}`}
                                                    onChange={ (option) => {onChangeFranchise(option)}}
                                                    // onInputChange={ (option) => {onChangeFranchise(option)}}
                                                    // value={search.franchiseId}
                                                    styles={customStylesSelect}
                                                />
                                            </div>
                                            { canAccess['list-search-by-postcode'] && 
                                                <div className="col-sm-2">
                                                    <select
                                                        className="form-control"
                                                        name="postcodeId"
                                                        onChange={(e) => {
                                                            setSearch({ ...search, postcodeId: e.target.value })
                                                        }}
                                                        value={search.postcodeId}
                                                    >
                                                        <option value="">Search By Postcode</option>
                                                        {postcodes.map((obj) => (
                                                            <option key={obj.id} value={obj.id}>
                                                                {obj.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            }
                                            <div className="col-sm-2">
                                                <input type="text" name='areaCode' className="form-control" value={search.areaCode} onChange={(e) => setSearch({ ...search, areaCode: e.target.value })} placeholder="Search By Area Code" />
                                            </div>
                                            <div className="col-sm-2">
                                                <input type="text" name='email' className="form-control" value={search.email} onChange={(e) => setSearch({ ...search, email: e.target.value })} placeholder="Search By Email" />
                                            </div>
                                            <div className="col-sm-2">
                                                <select
                                                    className="form-control"
                                                    name="status"
                                                    onChange={(e) => setSearch({ ...search, status: e.target.value })}
                                                    value={search.status}
                                                >
                                                    <option value="">Search By Status</option>
                                                    {centreStatus.map((obj) => (
                                                        <option key={obj.value} value={obj.value}>
                                                            {obj.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className='row mb-2'>
                                            <div className='col-sm-4'>
                                                <Button type="submit" isLoading={isLoading} onClick={handleResetFilter} className="btn btn-warning mr-2">Reset Filter</Button>
                                                { canAccess?.['export-csv'] &&
                                                    <Button type="submit" isLoading={isLoading} onClick={exportCSV} className="btn btn-success"><i className="fas fa-file-csv"></i> Export</Button>
                                                }
                                            </div>
                                        </div>

                                        <DataTable
                                            columns={columns}
                                            data={centers}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            paginationDefaultPage={currentPage}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            paginationPerPage={perPage}
                                            paginationRowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                                            progressPending={isLoading}
                                            sortServer
                                            onSort={handleSort}
                                            customStyles={customStyle}
                                        />
                                    </div>
                                    {/* /.card-body */}
                                </div>
                            </div>
                            {/* /.col */}
                        </div>
                        {/* /.row */}
                    </div>
                    {/* /.container-fluid */}
                </section>
                {/* /.content */}
            </div>

        </div>
    )
};

export default ListCenters;